// frontend/src/components/routing/legacyRedirects.jsx
// Brighella v2 (ADR-013 Merges A + B) — Legacy-route redirect components.
//
// Each redirect rewrites a pre-merge URL into the unified v2 surface,
// preserving query string and hash so saved-view deep links survive.
//
// Pulled out of App.jsx so unit tests can import them in isolation.

import { Navigate, useLocation, useParams } from 'react-router-dom'

/**
 * Merge A — Calendar Hub.
 * /v2/calendar, /calendar/overview, /daily, /schedule,
 * /crew/schedule, /crew-schedule → /calendar (search + hash preserved).
 */
export function ToCalendarHub() {
  const { search, hash } = useLocation()
  return <Navigate to={`/calendar${search}${hash}`} replace />
}

/**
 * Comms Phase 2 — /feed → /comms/feed (search + hash preserved).
 */
export function ToCommsFeed() {
  const { search, hash } = useLocation()
  return <Navigate to={`/comms/feed${search}${hash}`} replace />
}

/**
 * Comms Phase 2 — /comms/dm/:id (and bare /comms/dm) → /comms/chat/:id.
 * Search + hash preserved.
 */
export function ToCommsChat() {
  const { id } = useParams()
  const { search, hash } = useLocation()
  return <Navigate to={`/comms/chat${id ? `/${id}` : ''}${search}${hash}`} replace />
}

/**
 * Comms Phase 2 — /comms/{group,channel,space}/:id, /comms/department/:id,
 * /comms/company → /comms/groups[/:id]. company has no id → bare /comms/groups.
 */
export function ToCommsGroup() {
  const { id } = useParams()
  const { search, hash } = useLocation()
  return <Navigate to={`/comms/groups${id ? `/${id}` : ''}${search}${hash}`} replace />
}

/**
 * Season Canvas restore — the legacy /season and /season-overview URLs now land
 * on the Season tab of the People Hub (revived AKOS, Merge C+). Search + hash
 * preserved so saved-view deep links survive.
 */
export function ToSeasonOverview() {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', 'season')
  return <Navigate to={`/people?${params.toString()}${hash}`} replace />
}

/**
 * Merge B — Production-as-workspace.
 * /productions/:id/{pull-sheet,call-sheet,budget} → /productions/:id?tab=…
 * Existing query params on the legacy URL ride along; the `tab` key wins.
 */
export function ToProductionTab({ tab }: { tab: string }) {
  const { id } = useParams()
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', tab)
  return <Navigate to={`/productions/${id}?${params.toString()}${hash}`} replace />
}

/**
 * Merge B — Productions list view switch.
 * /incoming-productions, /external-productions, /department-orders
 *   → /productions?view=incoming|external|department-orders
 */
export function ToProductionsView({ view }: { view: string }) {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  params.set('view', view)
  return <Navigate to={`/productions?${params.toString()}${hash}`} replace />
}

/**
 * Merge C — People Hub.
 * /users, /admin/pto, /admin/freelancer-timesheets, /crew/workload
 *   → /people?tab=… (search + hash preserved).
 */
export function ToPeopleHub({ tab }: { tab?: string }) {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  if (tab) params.set('tab', tab)
  const qs = params.toString()
  return <Navigate to={`/people${qs ? `?${qs}` : ''}${hash}`} replace />
}

/**
 * Merge D — Reports Surface.
 * /financial/*, /reports/cost-comparison, /reports/depreciation, and the
 * three /admin/{low-stock,stale,lost-damaged,audit-log} reports
 *   → /reports?type=…&view=…
 *
 * Caller specifies the type + view explicitly; the redirect preserves
 * search + hash. The "tab" key is omitted intentionally — only type/view
 * are part of the canonical /reports query shape.
 */
export function ToReportsHub({ type, view }: { type?: string; view?: string }) {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  if (type) params.set('type', type)
  if (view) params.set('view', view)
  return <Navigate to={`/reports?${params.toString()}${hash}`} replace />
}

/**
 * Merge E — Movements Hub.
 * /loans, /borrowed, /network/loans → /movements?direction=…
 *
 * `direction=internal` is the default and gets omitted from the URL.
 */
export function ToMovementsHub({ direction }: { direction?: string }) {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  if (direction && direction !== 'internal') params.set('direction', direction)
  const qs = params.toString()
  return <Navigate to={`/movements${qs ? `?${qs}` : ''}${hash}`} replace />
}
